// MACHINERY V2 (Stroje) — Diely a materiál: toolbar, bulk bar, column header
// (M2.5).
//
// Split out of PartsTabV2.tsx for the 300-line cap. That file owns the states
// (the query and the selection moved to usePartsTabQuery.ts at the M2.5
// review); this owns the three chrome strips. Reference
// impl: design_handoff_machinery_v3/machinery-profile-parts.jsx (`PartsTab`
// toolbar + bulk bar, `PcHead`); artboard 1009.
//
// THE BULK BAR ONLY EXISTS WITH A SELECTION, which is what makes its destructive
// button safe to render in red without a permanent scary strip on the page.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";

import useTranslation from "../../../../../shared/hooks/useTranslation";
import {
  InstalledStockItemsQuery,
  InstalledStockItemsQueryParameter,
} from "../../../../inventory/models/models";
import { SortGlyph, TabSearch, headCellStyle } from "./tabAtoms";
import TabSelect, { TabSelectOption } from "./TabSelect";
import { PartCheckbox } from "./partsAtoms";
import { PC_GRID } from "./partsRows";

export const PartsToolbar: React.FC<{
  search: string;
  onSearchChange: (v: string) => void;
  query: InstalledStockItemsQuery;
  categories: TabSelectOption[];
  manufacturers: TabSelectOption[];
  onQueryChange: (next: InstalledStockItemsQuery) => void;
  onAdd: () => void;
}> = ({ search, onSearchChange, query, categories, manufacturers, onQueryChange, onAdd }) => {
  const { translate } = useTranslation("myMachinery");
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", minWidth: 0 }}>
      <TabSearch
        value={search}
        onChange={onSearchChange}
        placeholder={translate("myMachinery:searchByNumberOrAlternative")}
        clearLabel={translate("myMachinery:v2.tabs.clearSearch")}
      />
      <TabSelect
        label={translate("myMachinery:category")}
        anyLabel={translate("common:all")}
        value={query.categoryId}
        options={categories}
        onChange={(categoryId) => onQueryChange({ ...query, categoryId })}
      />
      <TabSelect
        label={translate("common:brand")}
        anyLabel={translate("common:all")}
        value={query.manufacturerId}
        options={manufacturers}
        onChange={(manufacturerId) => onQueryChange({ ...query, manufacturerId })}
      />
      <span style={{ flex: 1 }} />
      <button type="button" className="btn btn-mod" onClick={onAdd} style={{ whiteSpace: "nowrap" }}>
        <span aria-hidden style={{ fontSize: 15, lineHeight: 1, marginRight: 2 }}>
          +
        </span>
        {translate("myMachinery:addItem")}
      </button>
    </div>
  );
};

export const PartsBulkBar: React.FC<{
  count: number;
  busy: boolean;
  onUnlink: () => void;
  onClear: () => void;
}> = ({ count, busy, onUnlink, onClear }) => {
  const { translate } = useTranslation("myMachinery");
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "7px 12px",
        borderRadius: 8,
        background: "var(--mod-accent-soft)",
        border: "1px solid var(--mod-accent-line)",
        flexWrap: "wrap",
      }}
    >
      <span style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--ink-2)" }}>
        {translate("myMachinery:v2.tabs.parts.selected", { count })}
      </span>
      <span style={{ flex: 1 }} />
      <button
        type="button"
        className="btn"
        disabled={busy}
        onClick={onUnlink}
        style={{
          whiteSpace: "nowrap",
          color: "var(--red)",
          borderColor: "color-mix(in oklab, var(--red) 40%, var(--border))",
        }}
      >
        {translate("myMachinery:v2.tabs.parts.unlinkSelected", { count })}
      </button>
      <button type="button" className="btn btn-ghost" onClick={onClear}>
        {translate("myMachinery:v2.tabs.parts.clearSelection")}
      </button>
    </div>
  );
};

const sortButton = (
  label: string,
  active: boolean,
  desc: boolean | undefined,
  onClick: () => void
) => (
  <button
    type="button"
    onClick={onClick}
    style={{ ...headCellStyle, border: 0, background: "transparent", padding: 0, cursor: "pointer" }}
  >
    {label}
    <SortGlyph dir={active ? (desc ? "desc" : "asc") : undefined} />
  </button>
);

export const PartsHeadRow: React.FC<{
  query: InstalledStockItemsQuery;
  allSelected: boolean;
  someSelected: boolean;
  onToggleAll: () => void;
  onSort: (parameter: InstalledStockItemsQueryParameter) => void;
}> = ({ query, allSelected, someSelected, onToggleAll, onSort }) => {
  const { translate } = useTranslation("myMachinery");
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: PC_GRID,
        gap: 12,
        padding: "8px 14px",
        borderBottom: "1px solid var(--border)",
        alignItems: "center",
        minWidth: 0,
      }}
    >
      <PartCheckbox
        checked={allSelected}
        indeterminate={someSelected && !allSelected}
        label={translate("myMachinery:v2.tabs.parts.selectAll")}
        onChange={onToggleAll}
      />
      <span />
      {sortButton(
        translate("myMachinery:product"),
        query.sortBy === InstalledStockItemsQueryParameter.number,
        query.sortDesc,
        () => onSort(InstalledStockItemsQueryParameter.number)
      )}
      {sortButton(
        translate("myMachinery:brandCategory"),
        query.sortBy === InstalledStockItemsQueryParameter.manufacturer,
        query.sortDesc,
        () => onSort(InstalledStockItemsQueryParameter.manufacturer)
      )}
      <span style={headCellStyle}>{translate("myMachinery:installedInStock")}</span>
      <span style={{ ...headCellStyle, justifyContent: "flex-end" }}>
        {translate("myMachinery:v2.tabs.parts.actions")}
      </span>
    </div>
  );
};
